/**
 * Colouring the globe by height, for the 3D map's Elevation surface.
 *
 * Cesium's ElevationRamp material shades the whole terrain by elevation, so the
 * colour is the data rather than an overlay confined to the field. The stops
 * come from the harvest's own DEM legend wherever there is one, which keeps the
 * 3D view, the 2D elevation layer and the legend panel telling the same story
 * with the same colours.
 */

export type RampStop = {
  /** Metres above the datum the DEM is expressed in. */
  height: number
  /** Any CSS colour the legend carries. */
  color: string
}

/**
 * Fallback ramp, low to high, for a harvest with no custom DEM legend.
 *
 * A perceptually ordered sequence rather than a rainbow: rainbows invent
 * boundaries that are not in the data, which on a field of gentle relief reads
 * as structure that is not there.
 */
export const DEFAULT_RAMP_COLORS = Object.freeze([
  '#2b4162',
  '#3f7d8c',
  '#7fb069',
  '#d8c057',
  '#c96f3a',
  '#8c3b2f',
])

type LegendLike = {
  num_ranges?: number
  ranges?: { c?: string[]; q?: number[] }
  legend_data?: LegendLike
}

/**
 * Pull ordered stops out of a DEM custom legend.
 *
 * The payload is sometimes wrapped in `legend_data`, matching how
 * components/map/wms-layer reads it. Returns null when the legend cannot
 * produce a usable ramp, so the caller can fall back rather than render
 * something misleading.
 */
export const rampStopsFromLegend = (legendData: unknown): RampStop[] | null => {
  const legend = legendData as LegendLike | undefined
  const data = legend?.legend_data ?? legend
  const colors = data?.ranges?.c
  const quantities = data?.ranges?.q
  if (!Array.isArray(colors) || !Array.isArray(quantities)) return null

  const stops: RampStop[] = []
  const count = Math.min(colors.length, quantities.length)
  for (let i = 0; i < count; i++) {
    const height = Number(quantities[i])
    const color = colors[i]
    if (!Number.isFinite(height) || typeof color !== 'string' || !color)
      continue
    stops.push({ height, color })
  }

  if (stops.length < 2) return null
  stops.sort((a, b) => a.height - b.height)
  // A legend whose stops all sit at one height cannot define a gradient.
  return stops[stops.length - 1].height > stops[0].height ? stops : null
}

/** Evenly spaced stops across a height range, for the fallback ramp. */
export const rampStopsFromRange = (
  minimumHeight: number,
  maximumHeight: number,
  colors: readonly string[] = DEFAULT_RAMP_COLORS,
): RampStop[] | null => {
  if (
    !Number.isFinite(minimumHeight) ||
    !Number.isFinite(maximumHeight) ||
    maximumHeight <= minimumHeight ||
    colors.length < 2
  ) {
    return null
  }

  const span = maximumHeight - minimumHeight
  return colors.map((color, index) => ({
    color,
    height: minimumHeight + (span * index) / (colors.length - 1),
  }))
}

/** The height range a set of stops covers. */
export const rampRange = (stops: RampStop[]) => ({
  minimumHeight: stops[0].height,
  maximumHeight: stops[stops.length - 1].height,
})

const RAMP_TEXTURE_WIDTH = 256

/**
 * Render the stops into the 1px-tall gradient Cesium samples.
 *
 * Stops are positioned by their real height rather than spread evenly, so
 * uneven legend breaks — which is what a quantile-based legend produces — land
 * in the right place.
 */
export const createRampImage = (
  stops: RampStop[],
  createCanvas: () => HTMLCanvasElement = () =>
    document.createElement('canvas'),
): HTMLCanvasElement | null => {
  const canvas = createCanvas()
  canvas.width = RAMP_TEXTURE_WIDTH
  canvas.height = 1

  const context = canvas.getContext('2d')
  if (!context) return null

  const { minimumHeight, maximumHeight } = rampRange(stops)
  const span = maximumHeight - minimumHeight
  const gradient = context.createLinearGradient(0, 0, RAMP_TEXTURE_WIDTH, 0)

  stops.forEach(({ height, color }) => {
    const offset = Math.min(1, Math.max(0, (height - minimumHeight) / span))
    gradient.addColorStop(offset, color)
  })

  context.fillStyle = gradient
  context.fillRect(0, 0, RAMP_TEXTURE_WIDTH, 1)
  return canvas
}

/**
 * Colour the globe by height, and restore whatever was there before.
 *
 * Returns a no-op when the ramp cannot be built, leaving the caller's plain
 * base colour in place.
 */
export const applyElevationRamp = (
  scene: any,
  stops: RampStop[] | null,
): (() => void) => {
  const Cesium = (globalThis as { Cesium?: any }).Cesium
  const globe = scene?.globe
  if (!globe || !Cesium?.Material?.fromType || !stops || stops.length < 2) {
    return () => {}
  }

  const image = createRampImage(stops)
  if (!image) return () => {}

  const { minimumHeight, maximumHeight } = rampRange(stops)
  const previous = globe.material

  globe.material = Cesium.Material.fromType('ElevationRamp', {
    image,
    minimumHeight,
    maximumHeight,
  })
  scene.requestRender?.()

  let restored = false
  return () => {
    if (restored) return
    restored = true
    globe.material = previous
    scene.requestRender?.()
  }
}
